import React, { useEffect, useMemo, useRef, useState } from 'react';
import { UnsavedChangesGuard } from '@RichTextEditor';
import { FlexLayout, GridLayout, Spacer } from '@Styled/utilities';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { TypedTestQuestionPayload } from '../../../Types/TestQuestion';
import { yupResolver } from '@hookform/resolvers/yup';
import { ChoiceSchema, MCQ_SCHEMA } from '../../../Schema/MCQ.schema';
import {
	Button,
	Checkbox,
	InputAdornment,
	ListItemText,
	MenuItem,
	Switch,
	Typography,
} from '@mui/material';
import { DevTool } from '@hookform/devtools';
import EdIcon from '@Components/UI/Utilities/EdIcon/EdIcon';
import EdFormControl from '@Components/UI/Inputs/EdFormControl/EdFormControl';
import { useTypedSelector } from '@Features/store';
import TagChip from '@Components/UI/Chips/TagChip/TagChip';
import {
	EdFormControlError,
	EdFormControlLabel,
} from '@Components/UI/Inputs/EdFormControlLabel/EdFormControlLabel';
import MCQOption from './MCQOption';
import { useSnackbar } from '@Providers/useSnackbar';
import { TestQuestionsRequester } from '../../../Services/TestQuestions/TestQuestionsRequester';
import { useHistory } from 'react-router-dom';
import { useGotoParentRoute } from '@Providers/Routes/RoutesProvider';
import RichTextEditor from '@Components/UI/RichTextEditor/RichTextEditor';
import AddTag from '../../AddTag';
import styled from 'styled-components';
import EdButtonMenu from '@Components/UI/Buttons/EdButtonMenu/EdButtonMenu';
// import EdButtonMenu from "@Components/UI/Buttons/EdButtonMenu/EdButtonMenu";

/**
 * Serves both choice types — they share every field, differing only in how
 * many options may be correct. Without the prop the MRQ route rendered this
 * form with `type: 'mcq'` hardcoded and quietly created an MCQ.
 */
const MCQ = ({ type = 'mcq' }: { type?: 'mcq' | 'mrq' }) => {
	const editorRef = useRef<any>(null);
	const [addedId, setAddedId] = useState<number | undefined>();
	const feedbackEditorRef = useRef<any>(null);
	const { dropdownTags } = useTypedSelector((state) => state.dropdowns);
	const { currentEntity } = useTypedSelector((state) => state.Tests);
	const { displaySnackbar } = useSnackbar();
	const history = useHistory();
	const defaultValues = useMemo(() => {
		return {
			test_id: currentEntity?.id ?? 0,
			multipleChoiceOptions: [
				{ choice: '', is_correct: false },
				{ choice: '', is_correct: false },
			],
			tags: [],
			weight: NaN,
			canAddChoice: false,
			type,
		};
	}, [type]);
	const { control, watch, formState, handleSubmit, setValue, reset, register } =
		useForm<TypedTestQuestionPayload<'mcq'>>({
			mode: 'all',
			resolver: async (...args) => {
				const r = await yupResolver(MCQ_SCHEMA)(...args);
				return r;
			},
			defaultValues,
		});

	const watchTags = watch('tags');
	const watchMCQ = watch('multipleChoiceOptions');
	const canAddChoice = useMemo(() => {
		const notValid = watchMCQ.find((_) => !ChoiceSchema.isValidSync(_));
		return notValid === undefined;
	}, [JSON.stringify(watchMCQ)]);
	const mcqError = !!watchMCQ.find((_) => _.is_correct);

	useEffect(() => {
		if (addedId !== undefined) {
			setValue('tags', [...watchTags, addedId]);
		}
	}, [addedId]);

	const canRemoveOption = useMemo(() => {
		return watchMCQ.length > 2;
	}, [watchMCQ]);

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'multipleChoiceOptions',
		keyName: 'opId',
	});
	const goToParent = useGotoParentRoute();

	const onSave = async (form: TypedTestQuestionPayload<'mcq'>) => {
		try {
			await TestQuestionsRequester.instance.create(form);
			displaySnackbar('success', 'Created question successfully');
			goToParent();
		} catch (error) {
			displaySnackbar('error', error?.response?.data?.message || 'Something went wrong');
		}
	};

	const { isSubmitting, isDirty, isValid, errors, dirtyFields } = formState;

	const onSaveWithReset = async (form: TypedTestQuestionPayload<'mcq'>) => {
		try {
			await TestQuestionsRequester.instance.create(form);
			displaySnackbar('success', 'Created question successfully');
			onReset();
		} catch (error) {
			displaySnackbar('error', error?.response?.data?.message || 'Something went wrong');
		}
	};

	const onReset = () =>
		reset({
			content: '',
			multipleChoiceOptions: [
				{ choice: '', is_correct: false },
				{ choice: '', is_correct: false },
			],
			tags: [],
			weight: NaN,
			feedback: '',
			// "Save and add another" must keep making the same kind of question.
			type,
			test_id: currentEntity?.id ?? 0,
		});

	return (
		<FlexLayout flexDirection="column" px="2.625rem" flex="1">
			<UnsavedChangesGuard formState={formState} />
			<FlexLayout flex="1">
				<Controller
					control={control}
					name="content"
					render={({ field: { value, onChange, ...props } }) => {
						return (
							<>
								<RichTextEditor preset="question"
									{...props}
									value={value}
									onChange={onChange}
								/>
							</>
						);
					}}
				/>
			</FlexLayout>
			{/* <Spacer my="1.5rem" /> */}

			<GridLayout my="1.5rem">
				<Controller
					control={control}
					name="type"
					render={({ field }) => (
						<FlexLayout alignItems={'center'}>
							<Typography>Single</Typography>
							<Switch
								onChange={(e) =>
									field.onChange(e.target.checked ? 'mrq' : 'mcq')
								}
								checked={field.value === 'mrq'}
								inputProps={{ 'aria-label': 'ant design' }}
							/>
							<Typography>Multiple</Typography>
						</FlexLayout>
					)}
				/>
			</GridLayout>
			<GridLayout gridTemplateColumns={'repeat(1,1fr)'} gridGap="1.875rem">
				<FlexLayout mb="1.875rem" flexDirection="column">
					{fields.map(({ opId, choice, is_correct }, index) => {
						return (
							<MCQOption
								key={opId}
								canRemoveOption={canRemoveOption}
								remove={remove}
								control={control}
								index={index}
							/>
						);
					})}

					<div>
						<Button
							disabled={!canAddChoice}
							onClick={() => append({ choice: '', is_correct: false })}
							variant="outlined"
							startIcon={<EdIcon>add</EdIcon>}
						>
							ADD ANSWER
						</Button>
					</div>

					{!mcqError && dirtyFields.multipleChoiceOptions && (
						<EdFormControlError error={!mcqError}>
							Choose at least one correct choice
						</EdFormControlError>
					)}
				</FlexLayout>
				<FlexLayout alignItems="center">
					<EdFormControl
						width="5rem"
						type="number"
						required
						control={control}
						name="weight"
						label="Weight"
					/>
					<Spacer mx="0.5rem" />
					<Typography fontWeight="600">Points</Typography>
				</FlexLayout>
				<FlexLayout gridGap="1rem">
					<EdFormControl
						label="Tags"
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<EdIcon mIconType="Regular">local_offer</EdIcon>
								</InputAdornment>
							),
						}}
						SelectProps={{
							multiple: true,
							renderValue: (value) => {
								const values = Array.from(value as number[]);

								return (
									<>
										{values.map((v) => {
											const tag = dropdownTags.find((_) => _.id === v);
											return <>{tag?.name},</>;
										})}
									</>
								);
							},
						}}
						control={control}
						name="tags"
						select
						multiple
						renderValues={(value) => {
							return (
								<>
									<Spacer mb="1rem" />
									<FlexLayout
										// maxWidth={"17rem"}
										overflow="auto"
										py={'0.4rem'}
										flexWrap="wrap"
										gridGap="0.5rem"
									>
										{dropdownTags
											.filter((_) => watchTags.includes(_.id))
											.map((tag) => {
												return (
													<TagChip
														bgColor={tag.theme}
														icon={<EdIcon>local_offer</EdIcon>}
														label={tag.name}
													/>
												);
											})}
									</FlexLayout>
								</>
							);
						}}
					>
						{dropdownTags.map((tag, index) => {
							return (
								<MenuItem value={tag.id} key={`tag-${tag.id}`}>
									<Checkbox
										sx={{ color: tag.theme }}
										checked={watchTags.includes(tag.id)}
									/>
									<ListItemText primary={tag.name} />
								</MenuItem>
							);
						})}
					</EdFormControl>
					<AddTag setAddedId={setAddedId} />
				</FlexLayout>
				<FlexLayout>
					<EdFormControlLabel>Feedback</EdFormControlLabel>
					<Controller
						control={control}
						name="feedback"
						render={({ field: { onChange, value, ...props }, fieldState }) => {
							return (
								<RichTextEditor preset="feedback"
									{...props}
									min_height={200}
									value={value}
									onChange={onChange}
								/>
							);
						}}
					/>
				</FlexLayout>
			</GridLayout>
			{!currentEntity.active && (
				<FlexLayout mt="1.688rem" justifyContent="flex-end">
					<Button
						variant="contained"
						color="warning"
						onClick={() => goToParent()}
					>
						Cancel
					</Button>
					<Spacer mx="0.5rem" />

					<EdButtonMenu
						onClick={handleSubmit(onSave)}
						disabled={isSubmitting || !isValid || !isDirty}
						variant="contained"
						MenuItems={
							<div>
								<MenuItem onClick={handleSubmit(onSaveWithReset)}>
									Save and add another
								</MenuItem>
							</div>
						}
					>
						Save
					</EdButtonMenu>
				</FlexLayout>
			)}
		</FlexLayout>
	);
};

export default MCQ;
